import { useState } from "react";
import { AlertCircle, Loader2, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MfaSetupDialog } from "@/components/auth/MfaSetupDialog";
import { authService } from "@/services";
import { useAuthContext } from "@/providers/AuthProvider";

interface MfaSetupSectionProps {
  email: string;
}

export function MfaSetupSection({ email }: MfaSetupSectionProps) {
  const { getClientInfo } = useAuthContext();
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [secretCode, setSecretCode] = useState("");
  const [session, setSession] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSetupMfa = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.setupMfaAuthenticated();
      setSecretCode(response.secretCode);
      setSession(response.session);
      setShowMfaSetup(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to initialize MFA setup"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyMfa = async (code: string): Promise<string[] | null> => {
    const response = await authService.verifyMfaSetupAuthenticated({
      session,
      code,
    });

    if (response.success && response.backupCodes) {
      return response.backupCodes;
    }

    throw new Error("Verification failed");
  };

  const handleMfaComplete = async () => {
    setShowMfaSetup(false);
    await getClientInfo();
  };

  return (
    <>
      {error && (
        <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <Button
        onClick={handleSetupMfa}
        disabled={isLoading}
        variant="outline"
        className="w-full"
      >
        {isLoading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Setting up...
          </>
        ) : (
          <>
            <Shield className="h-4 w-4 mr-2" />
            Enable Two-Factor Authentication
          </>
        )}
      </Button>

      <MfaSetupDialog
        open={showMfaSetup}
        onOpenChange={setShowMfaSetup}
        secretCode={secretCode}
        email={email}
        onVerify={handleVerifyMfa}
        onComplete={handleMfaComplete}
      />
    </>
  );
}
